import * as Highcharts from 'highcharts';

import {Component, Inject, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {ChartsM2} from 'shared/dtos/cockpit/charts/charts';
import {FunktionswertGesamtverdienst} from 'shared/dtos/cockpit/charts/funktionswert-gesamtverdienst';
import {DataStorageService} from 'shared/services/data-storage.service';
import {
  ChartTemplateService,
  ChartType,
  Xaxis,
  Yaxis
} from '../../../cockpit-shared/charts-templates/chart-template.service';
import {CockpitChartComponent} from '../../../cockpit-shared/cockpit-content-component';
import {MitarbeiterBase} from 'shared/dtos/mitarbeiterBase';

@Component({
  selector: 'app-chart-dialog-funktionswert',
  templateUrl: './chart-dialog-funktionswert.component.html',
  providers: [ChartTemplateService],
  standalone: false
})
export class ChartDialogFunktionswertComponent
  extends CockpitChartComponent
  implements OnInit
{
  funktionswertGesamtverdienst: FunktionswertGesamtverdienst;
  mitarbeiter: MitarbeiterBase[];

  constructor(
    @Inject(ChartTemplateService)
    private readonly chartTemplateService: ChartTemplateService,
    private readonly dataStorageService: DataStorageService,
    private readonly translateService: TranslateService
  ) {
    super();
  }

  async ngOnInit() {
    this.mitarbeiter = await this.dataStorageService.getMitarbeiter();

    this.dataStorageService.getChartsData().then((data: ChartsM2) => {
      this.funktionswertGesamtverdienst = data.funktionswertGesamtverdienst;
      this.initialize(this.funktionswertGesamtverdienst);

      this.translateService.onLangChange.subscribe(() => {
        const svgContainer = document.getElementById('svgChartFunktionswert');
        if (svgContainer) {
          this.initialize(this.funktionswertGesamtverdienst);
        }
      });
    });
  }

  exportChart() {
    this.chart.exportChart(
      {
        type: 'application/pdf',
        filename: 'my-pdf'
      },
      {}
    );
  }

  initialize(data: FunktionswertGesamtverdienst) {
    this.chartTemplateService.chartname = 'funktionswert';
    this.initHighCharts(data);
  }

  private initHighCharts(data: FunktionswertGesamtverdienst) {
    const series = JSON.parse(
      this.chartTemplateService.translateChart(data.series)
    );

    const lfnrLabel = this.translateService.instant(
      'm2.sprint2.ergebnis.tabPersonenebene.table.header.laufnummer'
    );
    series[0].data = series[0].data.map(arr => ({
      x: arr[0],
      y: arr[1],
      lfnr: this.getLfnr(arr[0], arr[1], 'M'),
      lfnrLabel
    }));
    series[1].data = series[1].data.map(arr => ({
      x: arr[0],
      y: arr[1],
      lfnr: this.getLfnr(arr[0], arr[1], 'F'),
      lfnrLabel
    }));

    const chartType = {
      type: 'scatter'
    } as ChartType;

    const chartTitle = {
      text: this.translateService.instant(
        'm2.sprint3.cockpit.card.chart.funktionswert.title'
      ),
      style: {color: '#333333', fontSize: '16px'}
    };

    const xAxis = {
      min: 10,
      max: 50,
      title: {
        text: this.translateService.instant(
          'm2.sprint3.cockpit.card.chart.funktionswert.x-Achse'
        )
      }
    } as unknown as Xaxis;

    const yAxis = {
      min: 0,
      title: {
        text: this.translateService.instant(
          'm2.sprint3.cockpit.card.chart.funktionswert.yAxis.title'
        )
      }
    } as unknown as Yaxis;

    const legend = {reversed: false};
    const tooltip = {
      useHTML: true,
      // eslint-disable-next-line object-shorthand
      formatter: function () {
        return `<svg width="18" height="18">${this.point.series.legendSymbol.element.outerHTML}</svg>${this.series.name}</b><br/>
        <span style="color:${this.point.series.color}">${this.point.lfnrLabel}</span>: <b>${this.point.lfnr}</b><br/>
        <span style="color:${this.point.series.color}">m2.sprint3.cockpit.card.chart.funktionswert.x-Achse</span>: <b>${this.point.x}</b><br/>
        <span style="color:${this.point.series.color}">m2.sprint3.cockpit.card.chart.funktionswert.monatslohn</span>: <span style="font-weight:bold;">${this.point.y}</span><br/>`;
      }
    };

    this.chartTemplateService.initializeChartValues(
      series,
      chartTitle,
      xAxis,
      yAxis,
      legend,
      null,
      tooltip,
      chartType
    );

    this.chartTemplateService.json.plotOptions = {
      series: {marker: {radius: 8}}
    };
    Highcharts.chart(
      'svgChartFunktionswert',
      this.chartTemplateService.json,
      thechart => {
        this.callBackFuncToNormalizeBarChart(thechart);

        // Observer pour détecter les changements de taille du conteneur
        const resizeObserver = new ResizeObserver(() => {
          if (this.chart) {
            this.chart.reflow();
          }
        });
        resizeObserver.observe(
          document.getElementById('svgChartFunktionswert')
        );
      }
    );
  }

  private callBackFuncToNormalizeBarChart(thechart: Highcharts.Chart) {
    this.chart = thechart;
    thechart.reflow();
  }

  private getLfnr(
    funktionswert: number,
    standardisierterGesamtVerdienst: number,
    geschlecht: 'F' | 'M'
  ) {
    return this.mitarbeiter
      .filter(
        ma =>
          ma.funktionswert === funktionswert &&
          ma.standardisierterGesamtVerdienst.toFixed(4) ===
            standardisierterGesamtVerdienst.toFixed(4) &&
          ma.geschlecht === geschlecht
      )
      .map(el => el.laufnummer)
      .join(', ');
  }
}
